import { PayloadAction } from '@reduxjs/toolkit';
import { U21Themes } from 'app/shared/u21-ui/components';

import { LocalStorageKeys } from 'app/shared/constants/localStorage';

import { u21CreateSlice } from 'app/shared/thunk/u21CreateSlice';
import { ThemePreference } from 'app/modules/theme/models';
import { resolveTheme } from 'app/modules/theme/utils';

interface ThemeState {
  mode: U21Themes;
  preference: ThemePreference;
}

const getInitialState = (): ThemeState => {
  const preference =
    localStorage.getItem(LocalStorageKeys.THEME_PREFERENCE) ?? 'system';

  // If we have a preference key, use it
  if (
    preference === 'light' ||
    preference === 'dark' ||
    preference === 'system'
  ) {
    const mode = resolveTheme(preference);
    return { mode, preference };
  }

  localStorage.removeItem(LocalStorageKeys.THEME_PREFERENCE);
  return { mode: resolveTheme('system'), preference: 'system' };
};

export const initialState: Readonly<ThemeState> = getInitialState();

export const themeSlice = u21CreateSlice({
  name: 'theme',
  initialState,
  reducers: {
    toggleTheme: (draft) => {
      const systemTheme = resolveTheme('system');
      if (draft.mode === systemTheme) {
        // Currently matching system theme → switch to the opposite explicit theme
        draft.mode =
          systemTheme === U21Themes.LIGHT ? U21Themes.DARK : U21Themes.LIGHT;
        draft.preference = draft.mode === U21Themes.DARK ? 'dark' : 'light';
      } else {
        // Currently on the opposite → go back to system
        draft.mode = systemTheme;
        draft.preference = 'system';
      }
      localStorage.setItem(LocalStorageKeys.THEME_PREFERENCE, draft.preference);
    },
    updateThemePreference: (draft, action: PayloadAction<ThemePreference>) => {
      draft.preference = action.payload;
      draft.mode = resolveTheme(action.payload);
      localStorage.setItem(LocalStorageKeys.THEME_PREFERENCE, action.payload);
    },
  },
});

export const THEME_SLICE_NAME = themeSlice.name;
export const { toggleTheme, updateThemePreference } = themeSlice.actions;
export default themeSlice.reducer;
